import { useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";
const NOVA = "__nova__";

/**
 * Categoria do material — Brocas, Blocos, Resinas, Embalagem.
 *
 * É como o laboratório organiza o próprio estoque, e é ele quem cria as opções: não há lista
 * fechada porque cada laboratório agrupa do jeito dele.
 *
 * Arquivo próprio (e não dentro de `MaterialCamposDeCompra`) porque este campo abre o formulário,
 * antes da subcategoria, enquanto o resto dos dados de compra vem depois do nome.
 *
 * Atenção ao nome: no banco isto é `MaterialSubcategory`. Ver a nota em `MaterialCategory` no
 * `schema.prisma` — os nomes do código e da tela são trocados de propósito.
 */
export function MaterialCategoriaLab({
  valor,
  onChange,
}: {
  valor: string;
  onChange: (subcategoryId: string) => void;
}) {
  const utils = trpc.useUtils();
  const [criando, setCriando] = useState(false);
  const [nova, setNova] = useState("");

  const subsQuery = trpc.prosthesis.catalog.myMaterialSubcategories.useQuery();

  const criar = trpc.prosthesis.catalog.createMaterialSubcategory.useMutation({
    onSuccess: (sub) => {
      utils.prosthesis.catalog.myMaterialSubcategories.invalidate();
      onChange(sub.id);
      setCriando(false);
      setNova("");
    },
    onError: (e) => toast.error(e.message || "Erro ao criar categoria."),
  });

  return (
    <div>
      <p className={labelCls}>Categoria</p>
      {criando ? (
        <div className="flex gap-2">
          <input
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            placeholder="Ex: Blocos, Brocas, Embalagem"
            className={inputCls}
            autoFocus
          />
          <button
            type="button"
            disabled={criar.isPending || nova.trim().length < 2}
            onClick={() => criar.mutate({ label: nova.trim() })}
            className="inline-flex h-9 shrink-0 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            Criar
          </button>
          <button
            type="button"
            onClick={() => {
              setCriando(false);
              setNova("");
            }}
            className="inline-flex h-9 shrink-0 items-center rounded-md border border-zinc-200 px-3 text-sm text-zinc-600 dark:border-border dark:text-muted-foreground"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <select
          value={valor}
          onChange={(e) => (e.target.value === NOVA ? setCriando(true) : onChange(e.target.value))}
          className={inputCls}
        >
          <option value="">Sem categoria</option>
          {(subsQuery.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
          <option value={NOVA}>+ Nova categoria</option>
        </select>
      )}
    </div>
  );
}
